/**
 * Language switcher: emits a real link to the same page in the other locale.
 *
 * This is not a state toggle (no more onClick). It's an <a> link to
 * /ar/current-page when on /en/current-page, and vice versa. This approach:
 *
 * 1. Preserves accessibility (keyboard navigation, opening in new tabs)
 * 2. Works without client-side JS (graceful degradation)
 * 3. Signals to search engines that these are language variants (via hreflang)
 * 4. Respects the page context (stays on same page when switching language)
 */

import { Languages } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { localizedPath } from "@/lib/locale";
import type { Locale } from "@/content/types";

const LanguageSwitcher = () => {
  const { language } = useLanguage();
  const location = useLocation();

  const otherLocale: Locale = language === "en" ? "ar" : "en";

  /*
   * Only swap the locale segment when the current path actually has one.
   *
   * The 404 route lives at the root ("/404", deliberately — vite.config.ts
   * promotes it to dist/404.html) and so has no locale prefix. Blindly
   * prefixing it produced a link to "/ar/404", a page that does not exist:
   * the switcher on the error page was itself a broken link. Anything outside
   * the locale trees falls back to that locale's home instead.
   */
  const hasLocalePrefix = /^\/(en|ar)(\/|$)/.test(location.pathname);
  const otherPagePath = hasLocalePrefix
    ? localizedPath(otherLocale, location.pathname)
    : `/${otherLocale}`;
  const otherLabel = otherLocale === "en" ? "EN" : "AR";

  return (
    <a
      href={otherPagePath}
      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md hover:bg-slate-700 transition-colors"
      aria-label={`Switch to ${otherLocale === "en" ? "English" : "Arabic"}`}
      title={`${otherLocale === "en" ? "English" : "العربية"}`}
    >
      <Languages className="w-4 h-4" />
      <span>{otherLabel}</span>
    </a>
  );
};

export default LanguageSwitcher;
